import React, { useState, useEffect } from 'react';
import { BrandLogo } from '../components/BrandLogo';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Calendar, Clock, MapPin, Building2, Share2,
  Copy, Check, AlertCircle, ExternalLink, CheckCircle,
  XCircle, Loader2, Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_TYPES = {
  seminar: 'Seminar',
  workshop: 'Workshop',
  panel_discussion: 'Panel Discussion',
  networking: 'Networking Event',
  conference: 'Conference',
  roundtable: 'Roundtable',
  webinar: 'Webinar',
  other: 'Event'
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });
  } catch { return dateStr; }
};

export default function InvitationPage() {
  const { invitationId } = useParams();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showRSVP, setShowRSVP] = useState(null); // 'interested' | 'declined' | null

  useEffect(() => {
    const fetchInvitation = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/invite/${invitationId}`);
        setInvitation(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invitation not found');
      } finally {
        setLoading(false);
      }
    };
    fetchInvitation();
  }, [invitationId]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const shareViaWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(`${invitation.event_title} — ${window.location.href}`)}`, '_blank');
  const shareViaLinkedIn = () => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank');
  const shareViaEmail = () => window.open(`mailto:?subject=${encodeURIComponent(invitation.event_title)}&body=${encodeURIComponent(`You're invited: ${window.location.href}`)}`, '_blank');

  if (loading) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#C41E3A]" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="text-center">
        <AlertCircle className="w-16 h-16 text-[#C41E3A] mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">Invitation Not Found</h1>
        <p className="text-zinc-400 mb-6">{error}</p>
        <button onClick={() => navigate('/')} className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-xl font-medium transition-colors">
          Go to BrandStage
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="invitation-page">
      {/* Branded Header */}
      <div className="bg-gradient-to-r from-[#C41E3A] to-[#8B1429]">
        <div className="max-w-4xl mx-auto px-4 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {invitation.company_logo ? (
              <img src={invitation.company_logo} alt={invitation.company_name} className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl object-contain bg-white/10 p-1 border border-white/20" />
            ) : (
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-white/20 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white/80" />
              </div>
            )}
            <div>
              <p className="text-white/70 text-xs sm:text-sm">Invitation from</p>
              <p className="text-white font-bold text-lg sm:text-xl">{invitation.company_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={copyLink} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" title="Copy link">
              {copied ? <Check className="w-4 h-4 text-emerald-300" /> : <Copy className="w-4 h-4 text-white/80" />}
            </button>
            <button onClick={shareViaWhatsApp} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" title="WhatsApp">
              <Share2 className="w-4 h-4 text-green-300" />
            </button>
            <button onClick={shareViaEmail} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" title="Email">
              <Mail className="w-4 h-4 text-white/80" />
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Expired Notice */}
        {invitation.is_expired && (
          <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <p className="text-amber-200 text-sm">This event has already taken place.</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
              {/* Banner */}
              {invitation.banner_image_url ? (
                <div className="aspect-[3/1] bg-zinc-800">
                  <img src={invitation.banner_image_url} alt={invitation.event_title} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="h-24 bg-gradient-to-br from-[#C41E3A]/30 to-[#1E3A5F]/30" />
              )}

              <div className="p-6">
                {/* Event Type Badge */}
                <span className="inline-block bg-[#C41E3A]/20 text-[#C41E3A] text-xs font-medium px-3 py-1 rounded-full mb-4">
                  {EVENT_TYPES[invitation.event_type] || invitation.event_type}
                </span>

                {/* Title */}
                <h1 className="text-2xl md:text-3xl font-bold text-white mb-6" data-testid="invitation-title">
                  {invitation.event_title}
                </h1>

                {/* Date, Time, Venue cards */}
                <div className="grid sm:grid-cols-3 gap-3 mb-6">
                  <div className="bg-zinc-800/50 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1">
                      <Calendar className="w-4 h-4 text-[#C41E3A]" /> Date
                    </div>
                    <p className="text-white font-medium text-sm">{formatDate(invitation.event_date)}</p>
                  </div>
                  {invitation.event_time && (
                    <div className="bg-zinc-800/50 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1">
                        <Clock className="w-4 h-4 text-[#C41E3A]" /> Time
                      </div>
                      <p className="text-white font-medium text-sm">{invitation.event_time}</p>
                    </div>
                  )}
                  <div className="bg-zinc-800/50 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1">
                      <MapPin className="w-4 h-4 text-[#C41E3A]" /> Venue
                    </div>
                    <p className="text-white font-medium text-sm">{invitation.venue}</p>
                  </div>
                </div>

                {/* Description */}
                {invitation.description && (
                  <div className="mb-6">
                    <h3 className="text-zinc-400 text-sm font-medium mb-2">About This Event</h3>
                    <p className="text-zinc-300 leading-relaxed whitespace-pre-line text-sm">{invitation.description}</p>
                  </div>
                )}

                {/* Objectives */}
                {invitation.objectives && (
                  <div className="mb-6">
                    <h3 className="text-zinc-400 text-sm font-medium mb-2">Objectives</h3>
                    <p className="text-zinc-300 leading-relaxed whitespace-pre-line text-sm">{invitation.objectives}</p>
                  </div>
                )}

                {/* Share */}
                <div className="border-t border-zinc-800 pt-6 mt-6">
                  <h3 className="text-zinc-400 text-sm font-medium mb-3">Share This Invitation</h3>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={copyLink} className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white px-4 py-2.5 rounded-lg text-sm transition-colors">
                      {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                      {copied ? 'Copied!' : 'Copy Link'}
                    </button>
                    <button onClick={shareViaWhatsApp} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-lg text-sm transition-colors">
                      <Share2 className="w-4 h-4" /> WhatsApp
                    </button>
                    <button onClick={shareViaLinkedIn} className="flex items-center gap-2 bg-[#0A66C2] hover:bg-[#004182] text-white px-4 py-2.5 rounded-lg text-sm transition-colors">
                      <ExternalLink className="w-4 h-4" /> LinkedIn
                    </button>
                    <button onClick={shareViaEmail} className="flex items-center gap-2 bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-2.5 rounded-lg text-sm transition-colors">
                      <Mail className="w-4 h-4" /> Email
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar — RSVP */}
          <div className="space-y-5">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 sticky top-6" data-testid="rsvp-sidebar">
              {/* Company identity */}
              <div className="flex items-center gap-3 mb-4 pb-4 border-b border-zinc-800">
                {invitation.company_logo ? (
                  <img src={invitation.company_logo} alt="" className="w-10 h-10 rounded-lg object-contain bg-white/5 p-0.5" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-zinc-500" />
                  </div>
                )}
                <div>
                  <p className="text-white text-sm font-semibold">{invitation.company_name}</p>
                  <p className="text-zinc-500 text-xs">Event Host</p>
                </div>
              </div>

              <h3 className="text-white font-bold mb-1">RSVP</h3>
              <p className="text-zinc-400 text-sm mb-4">Respond to this invitation</p>

              {!invitation.is_expired ? (
                <div className="space-y-2">
                  <button
                    onClick={() => setShowRSVP('interested')}
                    className="w-full bg-[#C41E3A] hover:bg-[#a31830] text-white font-bold py-3 rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
                    data-testid="express-interest-btn"
                  >
                    <CheckCircle className="w-4 h-4" /> Express Interest
                  </button>
                  <button
                    onClick={() => setShowRSVP('declined')}
                    className="w-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-medium py-3 rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
                    data-testid="decline-btn"
                  >
                    <XCircle className="w-4 h-4" /> Decline
                  </button>
                </div>
              ) : (
                <div className="text-center py-3 bg-zinc-800 rounded-lg text-zinc-500 text-sm">
                  This event has already taken place
                </div>
              )}

              {/* View Profile */}
              {invitation.profile_url && (
                <div className="mt-4 pt-4 border-t border-zinc-800">
                  <Link to={invitation.profile_url} className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm transition-colors">
                    <Building2 className="w-4 h-4" /> View {invitation.company_name} Profile
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* RSVP Modals */}
      {showRSVP === 'interested' && (
        <InviteRSVPModal invitationId={invitationId} eventTitle={invitation.event_title} companyName={invitation.company_name} onClose={() => setShowRSVP(null)} />
      )}
      {showRSVP === 'declined' && (
        <InviteDeclineModal invitationId={invitationId} eventTitle={invitation.event_title} onClose={() => setShowRSVP(null)} />
      )}

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Link to="/" className="text-zinc-400 hover:text-white text-sm">
            Powered by <BrandLogo className="h-6 inline-block align-middle ml-1" />
          </Link>
        </div>
      </footer>
    </div>
  );
}

// ---- Express Interest Modal ----
function InviteRSVPModal({ invitationId, eventTitle, companyName, onClose }) {
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', designation: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { alert('Name and email are required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/invitations/${invitationId}/rsvp`, { ...form, rsvp_status: 'interested' });
      setSuccess(true);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to submit'); }
    setSubmitting(false);
  };

  if (success) return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-8 max-w-md w-full text-center" data-testid="rsvp-success">
        <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-emerald-400" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">RSVP Confirmed!</h3>
        <p className="text-zinc-400 text-sm mb-2">Your interest in <strong className="text-white">"{eventTitle}"</strong> has been registered.</p>
        <div className="bg-zinc-800/50 rounded-lg p-3 mb-4 flex items-center gap-2 justify-center">
          <Mail className="w-4 h-4 text-[#C41E3A]" />
          <span className="text-zinc-300 text-sm">A confirmation email has been sent to <strong className="text-white">{form.email}</strong></span>
        </div>
        <p className="text-zinc-500 text-xs mb-6">{companyName} will be in touch shortly.</p>
        <button onClick={onClose} className="bg-[#C41E3A] hover:bg-[#a31830] text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-colors">Done</button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="rsvp-modal">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-lg">
        <div className="px-6 py-4 border-b border-zinc-700 flex items-center justify-between">
          <h3 className="text-white font-bold">Express Interest</h3>
          <button onClick={onClose} className="p-1 hover:bg-zinc-800 rounded"><span className="text-zinc-400 text-xl">&times;</span></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-400 block mb-1">Full Name *</label>
              <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C41E3A] focus:outline-none" required data-testid="rsvp-name" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 block mb-1">Company</label>
              <input value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C41E3A] focus:outline-none" data-testid="rsvp-company" />
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-400 block mb-1">Email *</label>
            <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C41E3A] focus:outline-none" required data-testid="rsvp-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-400 block mb-1">Phone</label>
              <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C41E3A] focus:outline-none" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 block mb-1">Designation</label>
              <input value={form.designation} onChange={e => setForm(p => ({ ...p, designation: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C41E3A] focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-400 block mb-1">Message</label>
            <textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} rows={3} placeholder="Any message for the organizer..." className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C41E3A] focus:outline-none resize-none" data-testid="rsvp-message" />
          </div>
          <button type="submit" disabled={submitting} className="w-full bg-[#C41E3A] hover:bg-[#a31830] disabled:opacity-50 text-white font-bold py-3 rounded-lg transition-colors text-sm flex items-center justify-center gap-2" data-testid="submit-rsvp-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            {submitting ? 'Submitting...' : 'Confirm Interest'}
          </button>
          <p className="text-zinc-600 text-[10px] text-center">You will receive a confirmation email upon submission.</p>
        </form>
      </div>
    </div>
  );
}

// ---- Decline Modal ----
function InviteDeclineModal({ invitationId, eventTitle, onClose }) {
  const [form, setForm] = useState({ name: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleDecline = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { alert('Name and email are required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/invitations/${invitationId}/rsvp`, { ...form, company: 'N/A', rsvp_status: 'declined' });
      setDone(true);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to submit'); }
    setSubmitting(false);
  };

  if (done) return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-8 max-w-md w-full text-center" data-testid="decline-success">
        <XCircle className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
        <h3 className="text-lg font-bold text-white mb-2">Response Noted</h3>
        <p className="text-zinc-400 text-sm mb-6">Thank you for letting us know. We hope to see you at a future event.</p>
        <button onClick={onClose} className="bg-zinc-800 hover:bg-zinc-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors">Close</button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="decline-modal">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-sm">
        <div className="px-6 py-4 border-b border-zinc-700 flex items-center justify-between">
          <h3 className="text-white font-bold text-sm">Decline Invitation</h3>
          <button onClick={onClose} className="p-1 hover:bg-zinc-800 rounded"><span className="text-zinc-400 text-xl">&times;</span></button>
        </div>
        <form onSubmit={handleDecline} className="p-6 space-y-3">
          <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Your name *" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-zinc-500 focus:outline-none" required />
          <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="Email *" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-zinc-500 focus:outline-none" required />
          <button type="submit" disabled={submitting} className="w-full bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-zinc-300 font-medium py-2.5 rounded-lg transition-colors text-sm flex items-center justify-center gap-2" data-testid="confirm-decline-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
            Confirm Decline
          </button>
        </form>
      </div>
    </div>
  );
}
